import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shirt, Plus, Package, TrendingUp, TrendingDown, AlertCircle, FileText, ArrowLeft, Settings, Filter as FilterIcon, RefreshCw, BookOpen, Scissors } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useAuth } from '@/contexts/AuthContext';
import { fetchInventarioActual, fetchMovimientos, recalcularInventario } from '@/services/movimientosLenceria.service';
import RegistrarEntradaModal from '@/components/inventario/RegistrarEntradaModal';
import AjustarInventarioModal from '@/components/inventario/AjustarInventarioModal';
import SeleccionarPrendaAjusteModal from '@/components/inventario/SeleccionarPrendaAjusteModal';
import ExportExcelButton from '@/components/ExportExcelButton';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import KardexMatrix from '@/components/lenceria/KardexMatrix';

const ITEMS_PER_PAGE = 50;

const InventarioLenceriaPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inventario');
  
  // Estados para Inventario Actual
  const [inventario, setInventario] = useState([]);
  const [isLoadingInventario, setIsLoadingInventario] = useState(false);
  const [currentPageInventario, setCurrentPageInventario] = useState(1);
  const [totalItemsInventario, setTotalItemsInventario] = useState(0);
  
  // Estados para Movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [isLoadingMovimientos, setIsLoadingMovimientos] = useState(false);
  const [currentPageMovimientos, setCurrentPageMovimientos] = useState(1);
  const [totalItemsMovimientos, setTotalItemsMovimientos] = useState(0);
  
  // Estados para Kardex (matriz prendas x movimientos)
  const [kardexMovimientos, setKardexMovimientos] = useState([]);
  const [isLoadingKardex, setIsLoadingKardex] = useState(false);

  // Estados para Modales
  const [isEntradaModalOpen, setIsEntradaModalOpen] = useState(false);
  const [isAjusteModalOpen, setIsAjusteModalOpen] = useState(false);
  const [selectedPrendaAjuste, setSelectedPrendaAjuste] = useState(null);
  const [isAjusteGeneralModalOpen, setIsAjusteGeneralModalOpen] = useState(false);
  const [isRecalculando, setIsRecalculando] = useState(false);
  
  // Estados para Filtros
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});
  
  // Estados para estadísticas
  const [stats, setStats] = useState({
    totalPrendas: 0,
    totalCantidad: 0,
    bajoStock: 0,
    agotados: 0
  });

  // Columnas para tabla de Inventario Actual
  const columnsInventario = [
    {
      id: 'tipo_prenda',
      label: 'Prenda',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Shirt className="h-4 w-4 text-purple-600" />
          <span className="font-medium">{row.tipo_prenda?.nombre || 'N/A'}</span>
        </div>
      )
    },
    {
      id: 'cantidad_disponible',
      label: 'Cantidad Disponible',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-green-50 text-green-700 font-semibold">
          {row.cantidad_disponible}
        </Badge>
      )
    },
    {
      id: 'cantidad_minima',
      label: 'Cantidad Mínima',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-600">{row.cantidad_minima}</span>
      )
    },
/*     {
      id: 'cantidad_en_lavado',
      label: 'En Lavado',
      render: (row) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          {row.cantidad_en_lavado || 0}
        </Badge>
      )
    }, */
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'disponible': 'bg-green-100 text-green-800',
          'bajo_stock': 'bg-yellow-100 text-yellow-800',
          'agotado': 'bg-red-100 text-red-800',
          'inactivo': 'bg-gray-100 text-gray-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'ultima_entrada',
      label: 'Última Entrada',
      render: (row) => row.ultima_entrada 
        ? new Date(row.ultima_entrada).toLocaleDateString('es-ES')
        : '-'
    }
  ];

  // Columnas para tabla de Movimientos
  const columnsMovimientos = [
    {
      id: 'fecha_movimiento',
      label: 'Fecha',
      sortable: true,
      render: (row) => new Date(row.fecha_movimiento).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      id: 'tipo_movimiento',
      label: 'Tipo',
      sortable: true,
      render: (row) => {
        const colors = {
          'entrada': 'bg-green-100 text-green-800',
          'salida': 'bg-red-100 text-red-800',
          'traslado': 'bg-blue-100 text-blue-800',
          'ajuste': 'bg-yellow-100 text-yellow-800',
          'baja': 'bg-gray-100 text-gray-800'
        };
        const icons = {
          'entrada': <TrendingUp className="h-3 w-3" />,
          'salida': <TrendingDown className="h-3 w-3" />,
          'traslado': <Package className="h-3 w-3" />,
          'ajuste': <AlertCircle className="h-3 w-3" />,
          'baja': <FileText className="h-3 w-3" />
        };
        return (
          <Badge className={`${colors[row.tipo_movimiento]} flex items-center gap-1 w-fit`}>
            {icons[row.tipo_movimiento]}
            {row.tipo_movimiento}
          </Badge>
        );
      }
    },
    {
      id: 'tipo_prenda',
      label: 'Prenda',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Shirt className="h-4 w-4 text-purple-600" />
          <span>{row.tipo_prenda?.nombre || 'N/A'}</span>
        </div>
      )
    },
    {
      id: 'cantidad',
      label: 'Cantidad',
      sortable: true,
      render: (row) => {
        const cantidad = row.cantidad;
        const isNegative = cantidad < 0;
        return (
          <Badge variant="outline" className="font-semibold">
            {isNegative ? '' : '+'}{cantidad}
          </Badge>
        );
      }
    },
    {
      id: 'numero_factura',
      label: 'Factura/Consecutivo',
      render: (row) => row.numero_factura || row.consecutivo || '-'
    },
    {
      id: 'usuario',
      label: 'Usuario',
      render: (row) => row.usuario?.nombre || 'N/A'
    },
    {
      id: 'cantidad_despues',
      label: 'Stock Resultante',
      render: (row) => (
        <span className="text-sm font-medium text-gray-700">
          {row.cantidad_despues !== undefined ? row.cantidad_despues : '-'}
        </span>
      )
    }
  ];

  // Fetch Inventario Actual
  const loadInventario = useCallback(async () => {
    setIsLoadingInventario(true);
    try {
      const response = await fetchInventarioActual({
        page: currentPageInventario,
        limit: ITEMS_PER_PAGE,
        ...filters
      });

      setInventario(response.inventario || []);
      setTotalItemsInventario(response.total || 0);

      // Calcular estadísticas
      const total = response.inventario?.length || 0;
      const totalCantidad = response.inventario?.reduce((sum, item) => sum + item.cantidad_disponible, 0) || 0;
      const bajoStock = response.inventario?.filter(item => item.estado === 'bajo_stock').length || 0;
      const agotados = response.inventario?.filter(item => item.estado === 'agotado').length || 0;

      setStats({
        totalPrendas: total,
        totalCantidad,
        bajoStock,
        agotados
      });
    } catch (error) {
      console.error('Error al cargar inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingInventario(false);
    }
  }, [currentPageInventario, filters, toast]);

  // Fetch Movimientos
  const loadMovimientos = useCallback(async () => {
    setIsLoadingMovimientos(true);
    try {
      const response = await fetchMovimientos({
        page: currentPageMovimientos,
        limit: ITEMS_PER_PAGE,
        ...filters
      });

      setMovimientos(response.movimientos || []);
      setTotalItemsMovimientos(response.total || 0);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar los movimientos',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingMovimientos(false);
    }
  }, [currentPageMovimientos, filters, toast]);

  // Cargar todos los movimientos para construir el Kardex (matriz)
  const loadKardex = useCallback(async () => {
    setIsLoadingKardex(true);
    try {
      const response = await fetchMovimientos({ page: 1, limit: 10000 });
      setKardexMovimientos(response.movimientos || []);
    } catch (error) {
      console.error('Error al cargar kardex:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el kardex',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingKardex(false);
    }
  }, [toast]);

  // Effects
  useEffect(() => {
    loadInventario();
  }, [loadInventario]);

  useEffect(() => {
    if (activeTab === 'movimientos') {
      loadMovimientos();
    }
  }, [activeTab, loadMovimientos]);

  // Recargar datos al cambiar de tab
  useEffect(() => {
    if (activeTab === 'inventario') {
      loadInventario();
    } else if (activeTab === 'movimientos') {
      loadMovimientos();
    } else if (activeTab === 'kardex') {
      loadKardex();
    }
  }, [activeTab]);

  // Handlers
  const handleEntradaSuccess = () => {
    setIsEntradaModalOpen(false);
    loadInventario();
    loadMovimientos();
    toast({
      title: 'Éxito',
      description: 'Entrada registrada correctamente',
    });
  };

  const handleAjusteSuccess = () => {
    setIsAjusteModalOpen(false);
    setSelectedPrendaAjuste(null);
    loadInventario();
    loadMovimientos();
    toast({
      title: 'Éxito',
      description: 'Ajuste registrado correctamente',
    });
  };

  const handleRecalcularInventario = async () => {
    const confirmar = window.confirm(
      '¿Recalcular el inventario a partir de los movimientos?\n\n' +
      'Esta acción sobrescribe las cantidades disponibles actuales usando la suma ' +
      'de todos los movimientos registrados. Úsala solo si se eliminaron movimientos de prueba.'
    );
    if (!confirmar) return;

    setIsRecalculando(true);
    try {
      const response = await recalcularInventario();
      toast({
        title: 'Inventario recalculado',
        description: response?.message ||
          `Se procesaron ${response?.prendas_procesadas ?? 0} prendas correctamente`,
      });
      await loadInventario();
      if (activeTab === 'movimientos') {
        await loadMovimientos();
      }
    } catch (error) {
      console.error('Error al recalcular inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo recalcular el inventario',
        variant: 'destructive'
      });
    } finally {
      setIsRecalculando(false);
    }
  };

  const handleSelectPrendaAjuste = (prenda) => {
    setSelectedPrendaAjuste(prenda);
    setIsAjusteGeneralModalOpen(false);
    setIsAjusteModalOpen(true);
  };

  // Configuración de columnas para exportar
  const exportColumnsInventario = [
    { header: 'Prenda', key: 'tipo_prenda.nombre' },
    { header: 'Cantidad Disponible', key: 'cantidad_disponible' },
    { header: 'Cantidad Mínima', key: 'cantidad_minima' },
    { header: 'En Lavado', key: 'cantidad_en_lavado' },
    { header: 'Estado', key: 'estado' },
    { 
      header: 'Última Entrada', 
      key: 'ultima_entrada',
      formatDate: true
    }
  ];

  const exportColumnsMovimientos = [
    { 
      header: 'Fecha', 
      key: 'fecha_movimiento',
      transform: (value) => value ? new Date(value).toLocaleString('es-ES') : '-'
    },
    { header: 'Tipo', key: 'tipo_movimiento' },
    { header: 'Prenda', key: 'tipo_prenda.nombre' },
    { header: 'Cantidad', key: 'cantidad' },
    { 
      header: 'Factura/Consecutivo', 
      key: 'numero_factura',
      transform: (value, item) => value || item.consecutivo || '-'
    },
    { header: 'Usuario', key: 'usuario.nombre' },
    { header: 'Stock Resultante', key: 'cantidad_despues' }
  ];

  // Configuración de filtros
  const filterFieldsInventario = [
    {
      id: 'search',
      label: 'Buscar por nombre',
      type: 'text'
    },
    {
      id: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'disponible', label: 'Disponible' },
        { value: 'bajo_stock', label: 'Bajo Stock' },
        { value: 'agotado', label: 'Agotado' },
        { value: 'inactivo', label: 'Inactivo' }
      ]
    }
  ];

  const filterFieldsMovimientos = [
    {
      id: 'tipo_movimiento',
      label: 'Tipo de Movimiento',
      type: 'select',
      options: [
        { value: 'entrada', label: 'Entrada' },
        { value: 'salida', label: 'Salida' },
        { value: 'traslado', label: 'Traslado' },
        { value: 'ajuste', label: 'Ajuste' },
        { value: 'baja', label: 'Baja' }
      ]
    },
    {
      id: 'fecha_movimiento',
      label: 'Rango de Fechas',
      type: 'daterange'
    }
  ];

  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    setCurrentPageInventario(1);
    setCurrentPageMovimientos(1);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setCurrentPageInventario(1);
    setCurrentPageMovimientos(1);
  };

  // Wrappers para servicios de exportación (compatible con ExportExcelButton)
  const exportInventarioService = async ({ query, limit, page }) => {
    const response = await fetchInventarioActual({ ...query, limit, page });
    return { data: response.inventario || [] };
  };

  const exportMovimientosService = async ({ query, limit, page }) => {
    const response = await fetchMovimientos({ ...query, limit, page });
    return { data: response.movimientos || [] };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/lavanderia')}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shirt className="h-8 w-8 text-purple-600" />
              Inventario de Lencería Central
            </h1>
            <p className="text-gray-600 mt-1">
              Gestión centralizada de inventario y movimientos de lencería
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate('/lavanderia/cortes-inventario')}
            variant="outline"
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-300"
          >
            <Scissors className="h-4 w-4" />
            Cortes de Inventario
          </Button>
          {user?.role === 'admin' && (
            <>
              <Button
                onClick={handleRecalcularInventario}
                disabled={isRecalculando}
                variant="outline"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
              >
                <RefreshCw className={`h-4 w-4 ${isRecalculando ? 'animate-spin' : ''}`} />
                {isRecalculando ? 'Recalculando...' : 'Recalcular Inventario'}
              </Button>
              <Button
                onClick={() => setIsAjusteGeneralModalOpen(true)}
                variant="outline"
                className="flex items-center gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-300"
              >
                <Settings className="h-4 w-4" />
                Ajustar/Dar de Baja
              </Button>
            </>
          )}
          <Button
            onClick={() => setIsEntradaModalOpen(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="h-4 w-4" />
            Registrar Entrada
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Prendas</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalPrendas}</p>
              </div>
              <Shirt className="h-8 w-8 text-purple-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cantidad Total</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalCantidad}</p>
              </div>
              <Package className="h-8 w-8 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bajo Stock</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.bajoStock}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Agotados</p>
                <p className="text-2xl font-bold text-red-600">{stats.agotados}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Gestión de Inventario </CardTitle>
          <CardDescription>
            Visualiza el inventario actual y el historial de movimientos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-4">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="inventario" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Inventario Actual
                </TabsTrigger>
                <TabsTrigger value="movimientos" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Movimientos
                </TabsTrigger>
                <TabsTrigger value="kardex" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Kardex
                </TabsTrigger>
              </TabsList>

              {/* Botones de Exportar y Filtrar */}
              {activeTab !== 'kardex' && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <FilterIcon className="h-4 w-4" />
                    Filtrar
                  </Button>
                  <ExportExcelButton
                    service={activeTab === 'inventario' ? exportInventarioService : exportMovimientosService}
                    filters={filters}
                    columns={activeTab === 'inventario' ? exportColumnsInventario : exportColumnsMovimientos}
                    fileName={`inventario_lenceria_${activeTab}_${new Date().toISOString().split('T')[0]}`}
                  />
                </div>
              )}
            </div>

            {/* Tab Inventario Actual */}
            <TabsContent value="inventario" className="space-y-4">
              {/* Filtros Aplicados */}
              <AppliedFilters
                filters={filters}
                fields={filterFieldsInventario}
                onRemoveFilter={handleRemoveFilter}
              />
              
              <DataTable
                columns={columnsInventario}
                data={inventario}
                isLoading={isLoadingInventario}
                showDefaultActions={false}
              />
              <Pagination
                currentPage={currentPageInventario}
                totalItems={totalItemsInventario}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageInventario}
              />
            </TabsContent>

            {/* Tab Movimientos */}
            <TabsContent value="movimientos" className="space-y-4">
              {/* Filtros Aplicados */}
              <AppliedFilters
                filters={filters}
                fields={filterFieldsMovimientos}
                onRemoveFilter={handleRemoveFilter}
              />
              
              <DataTable
                columns={columnsMovimientos}
                data={movimientos}
                isLoading={isLoadingMovimientos}
                showDefaultActions={false}
              />
              <Pagination
                currentPage={currentPageMovimientos}
                totalItems={totalItemsMovimientos}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageMovimientos}
              />
            </TabsContent>

            {/* Tab Kardex - Matriz Prendas x Movimientos */}
            <TabsContent value="kardex" className="space-y-4">
              <div className="text-sm text-gray-600">
                Matriz de movimientos: Prendas (filas) × Movimientos agrupados por consecutivo/factura (columnas)
              </div>
              <KardexMatrix movimientos={kardexMovimientos} isLoading={isLoadingKardex} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal de Entrada */}
      <RegistrarEntradaModal
        isOpen={isEntradaModalOpen}
        onClose={() => setIsEntradaModalOpen(false)}
        onSuccess={handleEntradaSuccess}
        user={user}
      />

      {/* Modal de Ajuste de Inventario */}
      <AjustarInventarioModal
        isOpen={isAjusteModalOpen}
        onClose={() => {
          setIsAjusteModalOpen(false);
          setSelectedPrendaAjuste(null);
        }}
        onSuccess={handleAjusteSuccess}
        prenda={selectedPrendaAjuste}
        user={user}
      />

      {/* Modal de Selección de Prenda para Ajuste */}
      <SeleccionarPrendaAjusteModal
        isOpen={isAjusteGeneralModalOpen}
        onClose={() => setIsAjusteGeneralModalOpen(false)}
        onSelectPrenda={handleSelectPrendaAjuste}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        fields={activeTab === 'inventario' ? filterFieldsInventario : filterFieldsMovimientos}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />
    </motion.div>
  );
};

export default InventarioLenceriaPage;
